import { useId } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { DashboardPageFormBody, DashboardPageFormField } from '@shigo-ui/dashboard-ui';
import { Input } from '@shigo-ui/ui';

import { ChoiceCardField } from '../../../../../../components/forms/ChoiceCardField';
import { dashboardT, type DashboardMessageKey } from '../../../../../../lib/messages';
import { PlacementPricingType, type PlacementPublisher } from '../../../model';
import type { PlacementFormValue } from '../../../placementFormSchema';

const pricingLabelKeys: Record<PlacementPricingType, DashboardMessageKey> = {
  [PlacementPricingType.None]: 'placements.pricing.inherit',
  [PlacementPricingType.RevenueShare]: 'placements.pricing.revshare',
  [PlacementPricingType.Cpm]: 'placements.pricing.cpm',
};

export function PlacementPricingFields(props: { publisher?: PlacementPublisher | undefined }) {
  'use no memo';
  const pricingId = useId();
  const { control, register, formState } = useFormContext<PlacementFormValue>();
  const pricingType = useWatch({ control, name: 'pricingType' });
  return (
    <DashboardPageFormBody width="lg">
      <Controller
        control={control}
        name="pricingType"
        render={({ field }) => (
          <ChoiceCardField
            label={dashboardT('placements.pricingType')}
            description={dashboardT('placements.pricing.chooseModel')}
            name={field.name}
            value={field.value}
            onValueChange={field.onChange}
            onBlur={field.onBlur}
            options={Object.values(PlacementPricingType).map((type) => ({
              value: type,
              label: dashboardT(pricingLabelKeys[type]),
              description: pricingDescription(type, props.publisher),
            }))}
          />
        )}
      />
      <div className="grid gap-5 md:grid-cols-2">
        {pricingType !== PlacementPricingType.None ? (
          <DashboardPageFormField
            htmlFor={`${pricingId}-amount`}
            label={dashboardT(
              pricingType === PlacementPricingType.RevenueShare
                ? 'placements.revenueShare'
                : 'placements.cpm',
            )}
            required
            error={formState.errors.pricing?.message}
          >
            <Input
              id={`${pricingId}-amount`}
              type="number"
              min="0"
              max={pricingType === PlacementPricingType.RevenueShare ? 100 : undefined}
              required
              aria-invalid={Boolean(formState.errors.pricing)}
              {...register('pricing')}
            />
            <p className="text-muted-foreground text-xs">
              {dashboardT(
                pricingType === PlacementPricingType.RevenueShare
                  ? 'placements.pricing.percentageRange'
                  : 'placements.pricing.cpmFloorHint',
              )}
            </p>
          </DashboardPageFormField>
        ) : null}
        <DashboardPageFormField
          htmlFor={`${pricingId}-margin`}
          label={dashboardT('placements.lineItemMargin')}
          error={formState.errors.lineItemMargin?.message}
        >
          <Input
            id={`${pricingId}-margin`}
            type="number"
            min="0"
            max="100"
            aria-invalid={Boolean(formState.errors.lineItemMargin)}
            {...register('lineItemMargin')}
          />
          <p className="text-muted-foreground text-xs">
            {dashboardT('placements.pricing.percentageRange')}
          </p>
        </DashboardPageFormField>
      </div>
    </DashboardPageFormBody>
  );
}

function pricingDescription(type: PlacementPricingType, publisher: PlacementPublisher | undefined) {
  if (type === PlacementPricingType.None) return inheritedPricingDescription(publisher);
  return dashboardT(
    type === PlacementPricingType.RevenueShare
      ? 'placements.pricing.revshareHint'
      : 'placements.pricing.cpmHint',
  );
}

function inheritedPricingDescription(publisher: PlacementPublisher | undefined) {
  const pricing = publisher?.allProductsCost;
  if (!publisher) return dashboardT('placements.pricing.selectPublisherHint');
  if (!pricing?.costType || pricing.value === undefined) {
    return dashboardT('placements.inheritedPricingEmpty');
  }
  return dashboardT('placements.pricing.inheritedHint', {
    publisher: publisher.name,
    type: pricing.costType,
    value: String(pricing.value),
  });
}
